import axios from 'axios';
import toast from 'react-hot-toast';
import useAuthStore from '../store/authStore'; // Import the auth store

// Create axios instance
const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5000/api',
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Get token directly from the auth store
    const token = useAuthStore.getState().token;
    
    if (token && typeof token === 'string') {
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      // Debug: warn when no token is available for a request that may need auth
      try {
        // eslint-disable-next-line no-console
        console.warn(`[api] No auth token set for request to ${config.url}`);
      } catch (e) {}
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response) {
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          // Unauthorized - trigger logout from the store. Do not force a full page reload.
          useAuthStore.getState().logout();
          // React Router will redirect since isAuthenticated becomes false
          break;
        case 403:
          toast.error('Access denied. You do not have permission to perform this action.');
          break;
        case 404:
          // Do not show toast for 404, as it can be noisy.
          // The component making the call should handle this.
          break;
        case 422:
          // Validation errors
          if (data && data.errors) {
            Object.values(data.errors).forEach(errorArray => {
              if(Array.isArray(errorArray)) {
                errorArray.forEach(error => toast.error(error));
              }
            });
          } else if (data && data.message) {
            toast.error(data.message);
          } else {
            toast.error('Validation failed.');
          }
          break;
        case 500:
          toast.error('Internal server error. Please try again later.');
          break;
        default:
          if (data && data.message) {
            toast.error(data.message);
          } else {
            toast.error('An error occurred. Please try again.');
          }
      }
    } else if (error.request) {
      // Network error
      toast.error('Network error. Please check your connection.');
    } else {
      // Other error
      toast.error('An unexpected error occurred.');
    }
    
    return Promise.reject(error);
  }
);

// API endpoints
export const endpoints = {
  // Auth
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
  },
  
  // Users
  users: {
    profile: '/users/profile',
    updateProfile: '/users/profile',
    getUser: (id) => `/users/${id}`,
  },
  
  // AI Recommendations
  ai: {
    courseRecommendations: '/ai/recommendations/courses',
    careerPathways: '/ai/recommendations/career-pathways',
    skillRecommendations: '/ai/recommendations/skills',
    jobRecommendations: '/ai/recommendations/jobs',
    personalizedDashboard: '/ai/recommendations/personalized-dashboard',
    // roadmap and chat endpoints
    roadmap: '/ai/roadmap',
    chats: {
      start: '/ai/chats/start',
      list: '/ai/chats',
      history: (id) => `/ai/chats/${id}/history`,
      message: (id) => `/ai/chats/${id}/message`,
    },
  },
  
  // Courses
  courses: {
    list: '/courses',
    get: (id) => `/courses/${id}`,
    create: '/courses',
    update: (id) => `/courses/${id}`,
    delete: (id) => `/courses/${id}`,
    enroll: (id) => `/courses/${id}/enroll`,
    progress: (id) => `/courses/${id}/progress`,
  },
  
  // Jobs
  jobs: {
    list: '/jobs',
    get: (id) => `/jobs/${id}`,
    apply: (id) => `/jobs/${id}/apply`,
    applications: '/jobs/applications',
  },
  
  // Skills
  skills: {
    list: '/skills',
    get: (id) => `/skills/${id}`,
    categories: '/skills/categories',
    userSkills: '/skills/user-skills',
    updateUserSkills: '/skills/user-skills',
  },
  
  // Analytics
  analytics: {
    learner: '/analytics/learner',
    trainer: '/analytics/trainer',
    policymaker: '/analytics/policymaker',
    skillsGap: '/analytics/skills-gap',
    jobMarket: '/analytics/job-market',
  },
};

export default api;
